import { Avatar } from '@mui/material';
import { GridToolbar } from '@mui/x-data-grid-pro';
import i18next from 'i18next';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';

import {
  NameCellContainer,
  NameSkuContainer,
  QuantityContainer,
  TooltipText,
  TotalCellContainer,
} from './styledComponents';

// Commons
import { booleanSelectOperators, createSelectOperators } from '@commons/utils/dataGrid';
import { convertUnits, getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import { getTheme } from '@commons/utils/theme';
import {
  TRANSFER_DETAILS_VIEW,
  TRANSFER_STATUS,
} from '@stocks/StocksInventories/Transfers/utils/constants';
import { translatedUnitCondensed } from '@commons/utils/translateUnit';
import { UnitNameWrapper } from '@stocks/StocksInventories/Transfers/components/TransferContent/RecipesDatagrid/styledComponents';
import DisplayNumber, { formatNumber } from '@commons/DisplayNumber';
import PackagingInfoDisplay from '@stocks/StocksInventories/Transfers/components/TransferContent/PackagingInfo';
import Text, { ENUM_COLORS, ENUM_FONTS, OVERFLOW_STYLE_TEXT } from '@commons/Text';
import TooltipDataGrid from '@commons/TooltipDataGrid';
import transferUtils from '@stocks/StocksInventories/Transfers/utils';

const renderTooltip = (text, tooltipText) => {
  const theme = getTheme();

  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.colors.greys.darkest,
            opacity: 1,
            padding: '16px',
          },
        },
      }}
      placement="bottom"
      title={<Text color={ENUM_COLORS.LIGHTEST}>{tooltipText}</Text>}
      arrow
    >
      <TooltipText>
        <div>{text}</div>
        <img alt="info-dgrey" src="/images/inpulse/info-dmgrey-small.svg" />
      </TooltipText>
    </Tooltip>
  );
};

const renderNameCell = (row, DEFAULT_IMAGE_URL) => {
  const theme = getTheme();

  return (
    <NameCellContainer>
      <Avatar
        alt="supplier-product-image"
        src={row.img || DEFAULT_IMAGE_URL}
        sx={{ width: 40, height: 40 }}
      />
      <NameSkuContainer>
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.colors.greys.darkest,
                opacity: 1,
                padding: '16px',
              },
            },
          }}
          placement={'top'}
          title={<Text color={ENUM_COLORS.LIGHTEST}>{row.name}</Text>}
          arrow
          followCursor
        >
          <TooltipText>
            <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16} style={OVERFLOW_STYLE_TEXT}>
              {row.name}
            </Text>
          </TooltipText>
        </Tooltip>

        {row.sku && (
          <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
            {row.sku}
          </Text>
        )}
      </NameSkuContainer>
    </NameCellContainer>
  );
};

const renderTextCell = (text) => <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>{text}</Text>;

const renderTextCellWithTooltip = (text, tooltipText) => {
  const theme = getTheme();

  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.colors.greys.darkest,
            opacity: 1,
            padding: '16px',
          },
        },
      }}
      placement={'top'}
      title={<Text color={ENUM_COLORS.LIGHTEST}>{tooltipText}</Text>}
      arrow
      followCursor
    >
      <TooltipText>
        <Text font={ENUM_FONTS.TEXT_BIG_HEIGHT_16} style={OVERFLOW_STYLE_TEXT}>
          {text}
        </Text>
      </TooltipText>
    </Tooltip>
  );
};

const renderSPTotalCell = (supplierProduct, currency, transferDetailsView) => {
  const { spTotal, totalQuantityInMasterUnit } = supplierProduct.packagings.reduce(
    (acc, packaging) => {
      acc.spTotal += packaging.selectedQuantity * packaging.price;

      acc.totalQuantityInMasterUnit += packaging.selectedQuantity * packaging.quantityInMasterUnit;

      return acc;
    },
    { spTotal: 0, totalQuantityInMasterUnit: 0 },
  );

  const isHighlighted = supplierProduct.packagings.some((packaging) =>
    transferUtils.hasDifferentQuantities(packaging, transferDetailsView),
  );

  return (
    <TotalCellContainer>
      <DisplayNumber
        currency={currency}
        font={
          isHighlighted
            ? ENUM_FONTS.TEXT_SMALL_HEIGHT_14_WEIGHT_700_BOLD
            : ENUM_FONTS.TEXT_SMALL_HEIGHT_14
        }
        number={spTotal}
        displayCurrencyCode
      />
      <Text
        color={ENUM_COLORS.DGREY}
        font={
          isHighlighted
            ? ENUM_FONTS.TEXT_SMALL_WEIGHT_600_HEIGHT_16
            : ENUM_FONTS.TEXT_SMALL_HEIGHT_16
        }
      >
        {`${formatNumber(totalQuantityInMasterUnit, 2)} ${translatedUnitCondensed(
          supplierProduct.supplierProductUnit,
        )}`}
      </Text>
    </TotalCellContainer>
  );
};

const renderRecipeTotalCell = (row, currency, transferDetailsView) => {
  const recipeTotal =
    row.selectedQuantity * getFactorToConvertMetricToEntityUnit(row.unit) * row.cost;

  const isHighlighted = transferUtils.hasDifferentQuantities(row, transferDetailsView);

  return (
    <TotalCellContainer>
      <DisplayNumber
        currency={currency}
        font={
          isHighlighted
            ? ENUM_FONTS.TEXT_SMALL_HEIGHT_14_WEIGHT_700_BOLD
            : ENUM_FONTS.TEXT_SMALL_HEIGHT_14
        }
        number={recipeTotal}
        displayCurrencyCode
      />
      <Text
        color={ENUM_COLORS.DGREY}
        font={
          isHighlighted
            ? ENUM_FONTS.TEXT_SMALL_WEIGHT_600_HEIGHT_16
            : ENUM_FONTS.TEXT_SMALL_HEIGHT_16
        }
      >
        {`${formatNumber(row.selectedQuantity, 2)} ${translatedUnitCondensed(
          convertUnits(row.unit),
        )}`}
      </Text>
    </TotalCellContainer>
  );
};

const _getQuantityHeaderTexts = (transferDetailsView) => {
  if (transferDetailsView === TRANSFER_DETAILS_VIEW.DRAFT) {
    return {
      header: i18next.t('STOCKS.TRANSFERS.DATAGRID_COLUMN_TRANSFERED_QUANTITY'),
      tooltip: i18next.t('STOCKS.TRANSFERS.BDT_QUANTITY_TOOLTIP'),
    };
  }

  if (transferDetailsView === TRANSFER_DETAILS_VIEW.DELIVERY) {
    return {
      header: i18next.t('STOCKS.TRANSFERS.DELIVERED_QUANTITY'),
      tooltip: i18next.t('STOCKS.TRANSFERS.DELIVERED_QUANTITY_TOOLTIP'),
    };
  }

  return {
    header: i18next.t('STOCKS.TRANSFERS.RECEIVED_QUANTITY'),
    tooltip: i18next.t('STOCKS.TRANSFERS.RECEIVED_QUANTITY_TOOLTIP'),
  };
};

export const getSupplierProductsDatagridColumns = ({
  renderPackagingCell,
  renderIncrementButtons,
  currency,
  transferList = {},
  transferDetailsView = TRANSFER_DETAILS_VIEW.DRAFT,
  isUserAssociatedToRecipient,
  subCategoryOptions = [],
  supplierNameOptions = [],
}) => {
  const { header: quantityHeaderName, tooltip: quantityTooltip } =
    _getQuantityHeaderTexts(transferDetailsView);

  const isDisabledDataGrid = [
    TRANSFER_DETAILS_VIEW.SENDER_DISABLED,
    TRANSFER_DETAILS_VIEW.DISABLED,
  ].includes(transferDetailsView);

  const columns = [
    {
      field: 'name',
      headerName: i18next.t('GENERAL.NAME'),
      minWidth: 360,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) =>
        renderNameCell(row, '/images/inpulse/supplier-product-photo-placeholder.svg'),
    },
    {
      field: 'subCategory',
      headerName: i18next.t('GENERAL.SUB_CATEGORIES'),
      width: 180,
      filterable: true,
      sortable: true,
      filterOperators: createSelectOperators(subCategoryOptions),
      renderCell: ({ row }) => {
        const subCategory = row.subCategory || i18next.t('GENERAL.NONE_VALUE');

        return renderTextCellWithTooltip(subCategory, subCategory);
      },
    },
    {
      field: 'lnkSupplierSupplierproductrel.name',
      headerName: i18next.t('GENERAL.SUPPLIER'),
      filterable: true,
      width: 180,
      valueGetter: ({ row }) => row.lnkSupplierSupplierproductrel?.name || '',
      filterOperators: createSelectOperators(supplierNameOptions),
      renderCell: ({ row }) => {
        const supplierName = row.lnkSupplierSupplierproductrel?.name || '';

        return renderTextCellWithTooltip(supplierName, supplierName);
      },
    },
    // Will not be displayed but present for filter purpose
    {
      field: 'sku',
      headerName: i18next.t('GENERAL.SKU'),
      filterable: true,
    },
    {
      field: 'isIngredientStrategic',
      headerName: i18next.t('GENERAL.STRATEGIC'),
      type: 'boolean',
      filterable: true,
      filterOperators: booleanSelectOperators,
    },
  ];

  if (transferList.status >= TRANSFER_STATUS.REQUESTED) {
    columns.push({
      field: 'packagingInfo',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TooltipDataGrid
          renderContent={() => (
            <PackagingInfoDisplay
              packagings={row.packagings}
              showRequested={
                isUserAssociatedToRecipient || transferList.status === TRANSFER_STATUS.REQUESTED
              }
              showSent={transferList.status >= TRANSFER_STATUS.SENT}
              transfers={row.transfers}
            />
          )}
        />
      ),
    });
  }

  columns.push({
    field: 'packagings',
    headerName: quantityHeaderName,
    width: 600,
    sortable: false,
    filterable: false,
    renderHeader: () => renderTooltip(quantityHeaderName, quantityTooltip),
    renderCell: ({ row }) => renderPackagingCell(row, isDisabledDataGrid),
  });

  if (!isDisabledDataGrid) {
    columns.push({
      field: 'incrementButtons',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => renderIncrementButtons(row),
    });
  }

  columns.push({
    field: 'total',
    headerName: i18next.t('GENERAL.TOTAL_EX_TAX'),
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => renderSPTotalCell(row, currency, transferDetailsView),
  });

  return columns;
};

export const getRecipesDatagridColumns = ({
  renderQuantityCell,
  renderIncrementButtons,
  currency,
  transferList = {},
  transferDetailsView = TRANSFER_DETAILS_VIEW.DRAFT,
  categoryOptions = [],
}) => {
  const { header: quantityHeaderName, tooltip: quantityTooltip } =
    _getQuantityHeaderTexts(transferDetailsView);

  const isDisabledDataGrid = [
    TRANSFER_DETAILS_VIEW.SENDER_DISABLED,
    TRANSFER_DETAILS_VIEW.DISABLED,
  ].includes(transferDetailsView);

  const transferNoteColumns = {
    field: 'transferNoteQuantity',
    headerName: i18next.t('STOCKS.TRANSFERS.DATAGRID_COLUMN_TRANSFERED_QUANTITY'),
    renderHeader: () =>
      renderTooltip(
        i18next.t('STOCKS.TRANSFERS.DATAGRID_COLUMN_TRANSFERED_QUANTITY'),
        i18next.t('STOCKS.TRANSFERS.BDT_QUANTITY_TOOLTIP'),
      ),
    width: 200,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => (
      <QuantityContainer>
        <Text color={ENUM_COLORS.IP_BLACK_4}>{row.transferNoteQuantity || '—'}</Text>{' '}
        <UnitNameWrapper>
          <Text color={ENUM_COLORS.IP_BLACK_4}>
            {translatedUnitCondensed(convertUnits(row.unit))}
          </Text>
        </UnitNameWrapper>
      </QuantityContainer>
    ),
  };

  const deliveryNoteColumns = {
    field: 'deliveryNoteQuantity',
    headerName: i18next.t('STOCKS.TRANSFERS.DELIVERED_QUANTITY'),
    renderHeader: () =>
      renderTooltip(
        i18next.t('STOCKS.TRANSFERS.DELIVERED_QUANTITY'),
        i18next.t('STOCKS.TRANSFERS.DELIVERED_QUANTITY_TOOLTIP'),
      ),
    width: 200,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => (
      <QuantityContainer>
        <Text color={ENUM_COLORS.IP_BLACK_4}>{row.deliveryNoteQuantity || '—'}</Text>{' '}
        <UnitNameWrapper>
          <Text color={ENUM_COLORS.IP_BLACK_4}>
            {translatedUnitCondensed(convertUnits(row.unit))}
          </Text>
        </UnitNameWrapper>
      </QuantityContainer>
    ),
  };

  const receivedColumns = {
    field: 'receivedQuantity',
    headerName: i18next.t('STOCKS.TRANSFERS.RECEIVED_QUANTITY'),
    renderHeader: () =>
      renderTooltip(
        i18next.t('STOCKS.TRANSFERS.RECEIVED_QUANTITY'),
        i18next.t('STOCKS.TRANSFERS.RECEIVED_QUANTITY_TOOLTIP'),
      ),
    width: 250,
    filterable: false,
    sortable: false,
    renderCell: ({ row }) => (
      <QuantityContainer>
        <Text color={ENUM_COLORS.IP_BLACK_4}>{row.receivedQuantity || '—'}</Text>{' '}
        <UnitNameWrapper>
          <Text color={ENUM_COLORS.IP_BLACK_4}>
            {translatedUnitCondensed(convertUnits(row.unit))}
          </Text>
        </UnitNameWrapper>
      </QuantityContainer>
    ),
  };

  const columns = [
    {
      field: 'name',
      headerName: i18next.t('GENERAL.NAME'),
      minWidth: 360,
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => renderNameCell(row, '/images/inpulse/recipe-photo-placeholder.svg'),
    },
    {
      field: 'category',
      headerName: i18next.t('GENERAL.CATEGORIES'),
      width: 180,
      sortable: true,
      filterable: true,
      filterOperators: createSelectOperators(categoryOptions),
      renderCell: ({ row }) => renderTextCell(row.category || i18next.t('GENERAL.NONE_VALUE')),
    },
    {
      field: 'brandsSearch',
      headerName: i18next.t('GENERAL.BRAND_PLURAL'),
      filterable: true,
      valueGetter: ({ row }) => (row.brands || []).map(({ name }) => name).join(' • '),
    },
  ];

  if (TRANSFER_DETAILS_VIEW.DELIVERY === transferDetailsView) {
    columns.push(transferNoteColumns);
  }

  if (
    [TRANSFER_DETAILS_VIEW.RECEPTION, TRANSFER_DETAILS_VIEW.SENDER_DISABLED].includes(
      transferDetailsView,
    )
  ) {
    columns.push(transferNoteColumns, deliveryNoteColumns);
  }

  if (TRANSFER_DETAILS_VIEW.DISABLED === transferDetailsView) {
    columns.push(transferNoteColumns, deliveryNoteColumns, receivedColumns);
  }

  // Allow increment / decrement quantities only if the view is not disabled
  if (!isDisabledDataGrid) {
    columns.push(
      {
        field: 'selectedQuantity',
        headerName: quantityHeaderName,
        width: (() => {
          switch (transferDetailsView) {
            case TRANSFER_DETAILS_VIEW.DELIVERY:
              return 350;
            case TRANSFER_DETAILS_VIEW.RECEPTION:
              return 150;
            default:
              return 600;
          }
        })(),
        sortable: false,
        filterable: false,
        renderHeader: () => renderTooltip(quantityHeaderName, quantityTooltip),
        renderCell: ({ row }) => renderQuantityCell(row),
      },
      {
        field: 'incrementButtons',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => renderIncrementButtons(row),
      },
    );
  }

  columns.push({
    field: 'total',
    headerName: i18next.t('GENERAL.TOTAL_EX_TAX'),
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => renderRecipeTotalCell(row, currency, transferDetailsView),
  });

  return columns;
};

export const DATAGRID_COMMON_PROPS = {
  disableColumnMenu: true,
  disableColumnResize: true,
  disableColumnSelector: true,
  disableDensitySelector: true,
  disableRowSelectionOnClick: true,
  ignoreDiacritics: true,
  slotProps: {
    toolbar: {
      showQuickFilter: true,
      printOptions: { disableToolbarButton: true },
      csvOptions: { disableToolbarButton: true },
    },
  },
};

export const DATAGRID_SLOT_PROPS = {
  toolbar: GridToolbar,
};
